import {
  buildAndroidExternalBrowserUrl,
  buildKakaoExternalBrowserTargetUrl,
} from "./kakaoExternalBrowser";

// Android Instagram's in-app browser ignores intent:// navigations without
// leaving the page, so a handoff that has not hidden the page by now is
// treated as blocked and Kakao login continues inside the in-app browser.
export const KAKAO_EXTERNAL_BROWSER_HANDOFF_TIMEOUT_MS = 1500;

export type KakaoExternalBrowserHandoffResult = "left_page" | "continued_in_app";

type StartKakaoLoginViaExternalBrowserParams = {
  next: string;
  continueInApp: () => void | Promise<void>;
  onBlocked?: () => void;
  currentUrl?: string;
  assignLocation?: (url: string) => void;
  documentObject?: Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">;
  windowObject?: Pick<Window, "addEventListener" | "removeEventListener" | "setTimeout" | "clearTimeout">;
  timeoutMs?: number;
};

export function startKakaoLoginViaExternalBrowser({
  next,
  continueInApp,
  onBlocked,
  currentUrl = window.location.href,
  assignLocation = (url) => window.location.assign(url),
  documentObject = document,
  windowObject = window,
  timeoutMs = KAKAO_EXTERNAL_BROWSER_HANDOFF_TIMEOUT_MS,
}: StartKakaoLoginViaExternalBrowserParams): Promise<KakaoExternalBrowserHandoffResult> {
  const targetUrl = buildKakaoExternalBrowserTargetUrl({ currentUrl, next });

  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      windowObject.clearTimeout(timeoutId);
      documentObject.removeEventListener("visibilitychange", handleVisibilityChange);
      windowObject.removeEventListener("pagehide", handlePageHide);
    };

    const markLeftPage = () => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve("left_page");
    };

    function handleVisibilityChange() {
      if (documentObject.visibilityState === "hidden") {
        markLeftPage();
      }
    }

    function handlePageHide() {
      markLeftPage();
    }

    const continueAfterBlockedHandoff = () => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      onBlocked?.();
      Promise.resolve()
        .then(continueInApp)
        .then(() => resolve("continued_in_app"), reject);
    };

    documentObject.addEventListener("visibilitychange", handleVisibilityChange);
    windowObject.addEventListener("pagehide", handlePageHide);

    const timeoutId = windowObject.setTimeout(() => {
      if (documentObject.visibilityState === "hidden") {
        markLeftPage();
        return;
      }
      continueAfterBlockedHandoff();
    }, timeoutMs);

    try {
      assignLocation(buildAndroidExternalBrowserUrl(targetUrl));
    } catch {
      continueAfterBlockedHandoff();
    }
  });
}
